"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  CheckCircle,
  XCircle,
  SkipForward,
  X,
  BadgeCheck,
  Loader2,
} from "lucide-react";
import axios from "axios";
import { base_url } from "../../config";
import BadgeButton from "../../components/BadgeButton/BadgeButton";

interface EurAIProofModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdf: Blob | null;
  row: any;
  onAccept: () => void | Promise<void>;
  onDecline: (reason: string) => void | Promise<void>;
  onSkip?: () => void | Promise<void>;
}

const DECLINE_REASONS = [
  "Your EurAI membership document is not readable",
  "Your document does not confirm a valid EurAI membership",
  "Your EurAI membership has expired or is not valid for the conference period",
  "Your EurAI membership document is not written in English",
  "The name on the membership document does not match your registration",
  "Other",
];

const typeChip = (value: string | undefined) => {
  switch (value) {
    case "student":
      return { label: "Student", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" };
    case "student-paper":
      return { label: "Student paper", cls: "bg-violet-500/15 text-violet-300 border-violet-500/30" };
    case "regular":
      return { label: "Regular", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" };
    default:
      return { label: value || "—", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" };
  }
};

export default function EurAIProofModal({
  isOpen,
  row,
  onClose,
  pdf,
  onAccept,
  onDecline,
  onSkip,
}: EurAIProofModalProps) {
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [textReason, setTextReason] = useState<string>("");
  const [isDeclineActive, setIsDeclineActive] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (pdf) {
      const url = URL.createObjectURL(pdf);
      setPdfUrl(url);
      return () => {
        URL.revokeObjectURL(url);
        setIsLoading(false);
      };
    }
  }, [pdf]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleClose();
      }
    }
    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        handleClose();
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "auto";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleAccept = async () => {
    try {
      setIsLoading(true);
      await axios.post(`${base_url}/api/admin/accept_eurai_proof`, {
        code: row.code,
      });
      resetForm();
      await Promise.resolve(onAccept());
    } catch (error) {
      console.error("Error accepting EurAI proof:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDecline = async () => {
    setIsDeclineActive(false);
    try {
      setIsLoading(true);
      await axios.post(`${base_url}/api/admin/decline_eurai_proof`, {
        reason: textReason,
        code: row.code,
      });
      resetForm();
      await Promise.resolve(onDecline(textReason));
    } catch (error) {
      console.error("Error declining EurAI proof:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedReason("");
    setTextReason("");
    setIsDeclineActive(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleReasonSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedReason(value);
    if (value !== "Other") {
      setTextReason(value);
    } else {
      setTextReason("");
    }
  };

  if (!isOpen) return null;

  const tchip = typeChip(row?.type);

  return (
    <div className="dark fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div
        ref={modalRef}
        className="relative flex flex-col bg-slate-900 text-slate-100 border border-slate-700/50 rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 inline-flex items-center justify-center h-8 w-8 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-700/50 transition-colors z-10"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 text-blue-400 animate-spin" />
          </div>
        ) : (
          <>
            <header className="px-5 py-3 border-b border-slate-700/40 bg-slate-900/40 flex items-center gap-3 pr-12">
              <BadgeCheck className="h-5 w-5 text-emerald-400 shrink-0" />
              <div className="flex flex-col min-w-0">
                <h2
                  id="modal-title"
                  className="text-base font-semibold text-slate-100 leading-tight truncate"
                >
                  {row?.name || "EurAI proof validation"}
                </h2>
                <span className="text-xs text-slate-400 truncate">
                  {row?.email}
                </span>
              </div>
              <div className="ml-auto flex items-center gap-2 shrink-0">
                <BadgeButton code={row?.code} />
                <span className="font-mono text-[11px] px-2 py-0.5 rounded-md bg-slate-700/40 text-slate-200 border border-slate-600/40">
                  {row?.code}
                </span>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${tchip.cls}`}
                >
                  {tchip.label}
                </span>
              </div>
            </header>

            <div className="flex-1 min-h-0 px-5 pt-4">
              <div className="h-full rounded-xl overflow-hidden border border-slate-700/40 bg-slate-950">
                {pdfUrl ? (
                  <iframe
                    src={`${pdfUrl}#toolbar=0&navpanes=0`}
                    className="w-full h-full"
                    title="EurAI proof document"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-500 text-sm">
                    No document available
                  </div>
                )}
              </div>
            </div>

            {isDeclineActive ? (
              <div className="px-5 py-4 border-t border-slate-700/40 bg-slate-900/40 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase tracking-wider text-slate-400">
                      Reason
                    </span>
                    <select
                      value={selectedReason}
                      onChange={handleReasonSelect}
                      className="bg-slate-900 border border-slate-700/60 rounded-md px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-rose-500/30"
                    >
                      <option value="" disabled>
                        Select a reason
                      </option>
                      {DECLINE_REASONS.map((reason) => (
                        <option key={reason} value={reason}>
                          {reason}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase tracking-wider text-slate-400">
                      {selectedReason === "Other"
                        ? "Custom reason"
                        : "Edit reason (if needed)"}
                    </span>
                    <textarea
                      placeholder={
                        selectedReason === "Other"
                          ? "Please specify the reason for declining"
                          : "Edit the reason if needed"
                      }
                      value={textReason}
                      onChange={(e) => setTextReason(e.target.value)}
                      className="bg-slate-900 border border-slate-700/60 rounded-md px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-rose-500/30 min-h-[60px]"
                    />
                  </label>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setIsDeclineActive(false)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-slate-600/30 text-slate-200 border border-slate-600/40 hover:bg-slate-600/50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDecline}
                    disabled={!selectedReason || !textReason.trim()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-rose-500/20 text-rose-200 border border-rose-500/30 hover:bg-rose-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    Confirm decline
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-5 py-3 border-t border-slate-700/40 bg-slate-900/40 flex items-center justify-between">
                <button
                  onClick={() => setIsDeclineActive(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-rose-500/15 text-rose-200 border border-rose-500/30 hover:bg-rose-500/25 transition-colors"
                >
                  <XCircle className="h-3.5 w-3.5" />
                  Decline
                </button>
                <div className="flex items-center gap-2">
                  {onSkip && (
                    <button
                      onClick={async () => {
                        await Promise.resolve(onSkip());
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-slate-600/30 text-slate-200 border border-slate-600/40 hover:bg-slate-600/50 transition-colors"
                    >
                      <SkipForward className="h-3.5 w-3.5" />
                      Skip
                    </button>
                  )}
                  <button
                    onClick={handleAccept}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-500/20 text-emerald-200 border border-emerald-500/30 hover:bg-emerald-500/30 transition-colors"
                  >
                    <CheckCircle className="h-3.5 w-3.5" />
                    Accept
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
